import { Injectable, effect, inject, signal, untracked } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { Router } from '@angular/router';
import { firstValueFrom, timeout } from 'rxjs';
import { UserSessionService } from './user-session.service';
import { AuthorizationStateService } from './authorization/authorization-state.service';
import { AuthorizationService } from './authorization/authorization.service';
import { FeatureFlag } from '../enums/feature-flags.enums';
import { FeatureFlagPermission } from '../enums/permissions.enums';
import { FeatureFlagUtilities } from '../utilities/feature-flag.utilities';
import { ApiBaseUrls, ApiControllers, MaintenanceConstants, SystemPageRoutes } from '../constants/constants';
import { MaintenanceProbeResult } from '../enums/maintenance.enums';
import { BYPASS_CACHE } from '../interceptor/caching.interceptor';
import { MaintenanceUtilities, SUPPRESS_MAINTENANCE_REDIRECT } from '../utilities/maintenance.utilities';

/**
 * Owns whether the dashboard is in maintenance mode, from either direction: the `system.maintenance`
 * flag read at session bootstrap, or a maintenance 503 on any API call.
 *
 * Recovery is found by probing a route the maintenance check does NOT exempt, so that it answers 503
 * while maintenance is on and normally once it is off. An exempt route - `/health/*`, the
 * effective-flags read, `authorization/{userId}` - keeps returning 200 throughout and would report
 * the site as up the whole time.
 */
@Injectable({ providedIn: 'root' })
export class MaintenanceService {
  private readonly router = inject(Router);
  private readonly userSessionService = inject(UserSessionService);
  private readonly authorizationStateService = inject(AuthorizationStateService);
  private readonly authorizationService = inject(AuthorizationService);
  private readonly httpClient = inject(HttpClient);

  public readonly isInMaintenance = signal<boolean>(false);

  constructor() {
    // Watched here rather than pushed from UserSessionService, which would make the two services
    // import each other and leave one undefined at module-evaluation time.
    //
    // The authorization state is a dependency as well as the flags, because the two load
    // independently: flags arriving first would read canBypass() as false and send a System
    // Administrator to the maintenance page, and nothing would take them back off it.
    effect(() => {
      this.userSessionService.activeOrganizationFeatureFlags();
      this.authorizationStateService.authorizationState();

      untracked(() => {
        if (this.isInMaintenance() && this.canBypass()) {
          this.exitMaintenance();
          return;
        }

        this.checkFlag();
      });
    });
  }

  private pollTimer: ReturnType<typeof setInterval> | undefined = undefined;
  private isPolling = false;
  // Where the user was when maintenance interrupted them, so they land back there rather than on the
  // dashboard. Not the maintenance page itself, which would strand them on it.
  private returnUrl: string | undefined = undefined;

  /**
   * A System Administrator keeps working through maintenance so they can verify the release. The
   * permission has to be held at user level: the same permission in a member role does not bypass.
   */
  public canBypass(): boolean {
    const authorizationState = this.authorizationStateService.authorizationState();

    if (!authorizationState) {
      return false;
    }

    return this.authorizationService.hasUserPermission(
      authorizationState.userPermissions,
      FeatureFlagPermission.Update
    );
  }

  /**
   * Asks the API whether it is in maintenance, without needing the caller to be signed in: the check
   * runs ahead of authorization, so an unauthenticated request is answered the same way. Used by the
   * landing route so nobody is offered a sign-in they cannot complete.
   *
   * Anything other than the maintenance response - including a 401, which is what this call gets
   * normally - means the site is up.
   */
  public async probe(): Promise<MaintenanceProbeResult> {
    const requestUrl = `${ApiBaseUrls.CORE()}${ApiControllers.LOCATIONS()}/${MaintenanceConstants.PROBE_PATH()}`;

    try {
      // Bounded: a half-open request would otherwise hang the landing guard that awaits this. A
      // TimeoutError is not an HttpErrorResponse, so it classifies as Unknown.
      await firstValueFrom(this.httpClient.get(requestUrl, {
        context: new HttpContext()
          .set(SUPPRESS_MAINTENANCE_REDIRECT, true)
          .set(BYPASS_CACHE, true)
      }).pipe(timeout(MaintenanceConstants.PROBE_TIMEOUT_MS())));

      return MaintenanceProbeResult.Available;
    } catch (error) {
      return MaintenanceUtilities.classifyProbeFailure(error);
    }
  }

  /** Strict: an unreadable flag is not maintenance. */
  public checkFlag(): void {
    if (FeatureFlagUtilities.isSwitchOn(this.userSessionService.activeOrganizationFeatureFlags(), FeatureFlag.Maintenance)) {
      this.enterMaintenance();
    }
  }

  /** Called by the interceptor when any API call returns the maintenance 503. */
  public enterMaintenance(): void {
    if (this.canBypass() || this.isInMaintenance()) {
      return;
    }

    const currentUrl = this.router.url;
    if (!currentUrl.startsWith(`/${SystemPageRoutes.MAINTENANCE().join('/')}`)) {
      this.returnUrl = currentUrl;
    }

    this.isInMaintenance.set(true);
    this.startPolling();
    this.router.navigate(SystemPageRoutes.MAINTENANCE());
  }

  public exitMaintenance(): void {
    if (!this.isInMaintenance()) {
      return;
    }

    this.stopPolling();
    this.isInMaintenance.set(false);

    const target = this.returnUrl ?? '/dashboard';
    this.returnUrl = undefined;
    this.router.navigateByUrl(target);
  }

  /**
    * The maintenance page reporting that it is on screen. A reload lands here with a fresh service
    * that has no idea it is in maintenance, and exitMaintenance refuses to move a user it does not
    * believe is stuck - so the poll would find the flag off and do nothing.
    */
  public resumeOnMaintenancePage(): void {
    this.isInMaintenance.set(true);
    this.startPolling();
  }

  public startPolling(): void {
    if (this.pollTimer !== undefined) {
      return;
    }

    // Immediately, not after the first interval: a reload onto this page is the likeliest moment for
    // maintenance to have already ended, and waiting a full minute to find that out reads as broken.
    void this.poll();
    this.pollTimer = setInterval(() => void this.poll(), MaintenanceConstants.POLL_INTERVAL_MS());
  }

  public stopPolling(): void {
    if (this.pollTimer !== undefined) {
      clearInterval(this.pollTimer);
      this.pollTimer = undefined;
    }
  }

  private async poll(): Promise<void> {
    // The interval keeps firing whether or not the previous probe finished; without this a slow API
    // accumulates a new in-flight probe every minute.
    if (this.isPolling) {
      return;
    }

    this.isPolling = true;

    try {
      await this.pollOnce();
    } finally {
      this.isPolling = false;
    }
  }

  private async pollOnce(): Promise<void> {
    // Only the application answering releases the user. Maintenance means still down; Unknown is a
    // deploy blip that proves nothing, and acting on it would push them into an API that is still
    // restarting, which bounces them straight back here.
    if (await this.probe() === MaintenanceProbeResult.Available) {
      this.exitMaintenance();
    }
  }

}
